import { PropertyResponse, CreatePropertyRequest } from '@/services/api';

export interface ImportValidationError {
  row: number;
  field: string;
  value: string;
  error: string;
}

export interface ImportResult {
  valid: ParsedProperty[];
  invalid: ImportValidationError[];
  duplicates: ParsedProperty[];
  summary: {
    totalRows: number;
    validRows: number;
    invalidRows: number;
    duplicateRows: number;
  };
}

export interface ParsedProperty {
  rowIndex: number;
  data: CreatePropertyRequest;
  analysisType?: string; // Separate field since it's not in CreatePropertyRequest
  originalData: Record<string, string>;
}

// Expected CSV headers based on export format
export const EXPECTED_HEADERS = [
  'Property Address',
  'City',
  'State',
  'ZIP Code',
  'Analysis Type',
  'Property Type',
  'Purchase Price',
  'Down Payment',
  'Loan Amount',
  'Interest Rate (%)',
  'Loan Term (Years)',
  'Annual Taxes',
  'Annual Insurance',
  'Monthly HOA',
  'Expected Monthly Rent',
  'Vacancy Rate (%)',
  'Maintenance Rate (%)',
  'Management Rate (%)',
  'After Repair Value',
  'Holding Period (Months)',
  'Selling Costs',
  'Renovation - Roof',
  'Renovation - Floors',
  'Renovation - Painting',
  'Renovation - General',
  'Total Renovation Costs',
  'Monthly Cash Flow', // Read-only calculated field
  'Annual Cash Flow', // Read-only calculated field
  'Cash-on-Cash Return (%)', // Read-only calculated field
  'Cap Rate (%)', // Read-only calculated field
  'Date Added' // Read-only field
];

// Legacy headers for backward compatibility
export const LEGACY_HEADERS = [
  'Property Address',
  'City',
  'State',
  'ZIP Code',
  'Analysis Type',
  'Property Type',
  'Purchase Price',
  'Down Payment',
  'Loan Amount',
  'Interest Rate (%)',
  'Loan Term (Years)',
  'Annual Taxes',
  'Annual Insurance',
  'Monthly HOA',
  'Expected Monthly Rent',
  'Total Renovation Costs',
  'Monthly Cash Flow',
  'Annual Cash Flow',
  'Cash-on-Cash Return (%)',
  'Cap Rate (%)',
  'Date Added'
];

// Required fields for property creation
export const REQUIRED_FIELDS = [
  'Property Address',
  'Purchase Price',
  'Down Payment',
  'Loan Amount',
  'Interest Rate (%)',
  'Loan Term (Years)',
  'Annual Taxes',
  'Annual Insurance'
];

export const parseCSVContent = (csvContent: string): string[][] => {
  const lines = csvContent.split('\n').filter(line => line.trim());
  const rows: string[][] = [];

  for (const line of lines) {
    const row: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"' && (i === 0 || line[i - 1] === ',')) {
        inQuotes = true;
      } else if (char === '"' && inQuotes && (i === line.length - 1 || line[i + 1] === ',')) {
        inQuotes = false;
      } else if (char === '"' && inQuotes && line[i + 1] === '"') {
        // Handle escaped quotes
        current += '"';
        i++; // Skip next quote
      } else if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    row.push(current.trim());
    rows.push(row);
  }

  return rows;
};

export const validateCSVHeaders = (headers: string[]): { valid: boolean; errors: string[]; isLegacyFormat: boolean } => {
  const errors: string[] = [];
  const trimmedHeaders = headers.map(h => h.trim());

  // Check if this is a legacy format
  const isLegacyFormat = LEGACY_HEADERS.every(header =>
    trimmedHeaders.includes(header) || !REQUIRED_FIELDS.includes(header)
  ) && !trimmedHeaders.some(header =>
    ['Vacancy Rate (%)', 'Renovation - Roof', 'Renovation - Floors'].includes(header)
  );

  const acceptedHeaders = isLegacyFormat ? LEGACY_HEADERS : EXPECTED_HEADERS;

  // Check for required headers
  for (const requiredField of REQUIRED_FIELDS) {
    if (!trimmedHeaders.includes(requiredField)) {
      errors.push(`Missing required column: "${requiredField}"`);
    }
  }

  // Check for unknown headers (warn but don't fail)
  const unknownHeaders = trimmedHeaders.filter(header =>
    header && !acceptedHeaders.includes(header) && !EXPECTED_HEADERS.includes(header)
  );

  if (unknownHeaders.length > 0) {
    errors.push(`Unknown columns found (will be ignored): ${unknownHeaders.join(', ')}`);
  }

  if (isLegacyFormat) {
    errors.push('Legacy CSV format detected. Some new fields will be set to default values.');
  }

  return {
    valid: errors.length === 0 || errors.every(error => error.includes('Unknown columns') || error.includes('Legacy CSV')),
    errors,
    isLegacyFormat
  };
};

export const parsePropertyFromRow = (row: string[], headers: string[], rowIndex: number): ParsedProperty => {
  const originalData: Record<string, string> = {};
  const trimmedHeaders = headers.map(h => h.trim());

  // Build original data map
  trimmedHeaders.forEach((header, index) => {
    originalData[header] = row[index] || '';
  });

  // Parse renovation costs - handle both individual fields and total
  const renovationCosts: Record<string, number> = {};
  const roofCost = parseNumber(originalData['Renovation - Roof']);
  const floorsCost = parseNumber(originalData['Renovation - Floors']);
  const paintingCost = parseNumber(originalData['Renovation - Painting']);
  const generalCost = parseNumber(originalData['Renovation - General']);
  const totalRenovationCost = parseNumber(originalData['Total Renovation Costs']);

  if (roofCost) renovationCosts.roof = roofCost;
  if (floorsCost) renovationCosts.floors = floorsCost;
  if (paintingCost) renovationCosts.painting = paintingCost;
  if (generalCost) renovationCosts.general = generalCost;

  // If we have a total but no individual costs, put it in general
  if (totalRenovationCost && Object.keys(renovationCosts).length === 0) {
    renovationCosts.general = totalRenovationCost;
  }

  // Convert to CreatePropertyRequest format
  const data: CreatePropertyRequest = {
    property_address: originalData['Property Address'] || '',
    city: originalData['City'] || undefined,
    state: originalData['State'] || undefined,
    zip_code: originalData['ZIP Code'] || undefined,
    property_type: parsePropertyType(originalData['Property Type']),
    purchase_price: parseNumber(originalData['Purchase Price']) || 0,
    down_payment: parseNumber(originalData['Down Payment']) || 0,
    loan_amount: parseNumber(originalData['Loan Amount']) || 0,
    interest_rate: parseNumber(originalData['Interest Rate (%)']) || 0,
    loan_term: parseNumber(originalData['Loan Term (Years)']) || 0,
    annual_taxes: parseNumber(originalData['Annual Taxes']) || 0,
    annual_insurance: parseNumber(originalData['Annual Insurance']) || 0,
    monthly_hoa_fees: parseNumber(originalData['Monthly HOA']) || undefined,
    expected_monthly_rent: parseNumber(originalData['Expected Monthly Rent']) || undefined,
    vacancy_rate: parseNumber(originalData['Vacancy Rate (%)']) || undefined,
    maintenance_rate: parseNumber(originalData['Maintenance Rate (%)']) || undefined,
    management_rate: parseNumber(originalData['Management Rate (%)']) || undefined,
    after_repair_value: parseNumber(originalData['After Repair Value']) || undefined,
    holding_period_months: parseNumber(originalData['Holding Period (Months)']) || undefined,
    selling_costs: parseNumber(originalData['Selling Costs']) || undefined,
    renovation_costs: Object.keys(renovationCosts).length > 0 ? renovationCosts : undefined
  };

  return {
    rowIndex,
    data,
    analysisType: parseAnalysisType(originalData['Analysis Type']) || 'buy-hold',
    originalData
  };
};

const parseNumber = (value: string): number | undefined => {
  if (!value || value.trim() === '') return undefined;
  
  // Remove currency symbols, commas, and extra spaces
  const cleaned = value.replace(/[$,\s]/g, '');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? undefined : parsed;
};

const parsePropertyType = (value: string): string | undefined => {
  if (!value || value.trim() === '') return undefined;
  
  const normalized = value.toLowerCase().replace(/\s+/g, '-');
  const validTypes = ['single-family', 'multi-family', 'condo', 'townhouse', 'duplex', 'apartment'];
  
  return validTypes.includes(normalized) ? normalized : undefined;
};

const parseAnalysisType = (value: string): string | undefined => {
  if (!value || value.trim() === '') return 'buy-hold';
  
  const normalized = value.toLowerCase().replace(/[^a-z]/g, '');
  
  if (normalized.includes('buyhold') || normalized.includes('buy') && normalized.includes('hold')) {
    return 'buy-hold';
  } else if (normalized.includes('flip') || normalized.includes('fix')) {
    return 'flip';
  } else if (normalized.includes('brrr')) {
    return 'brrr';
  }
  
  return 'buy-hold'; // Default
};

export const validateProperty = (property: ParsedProperty): ImportValidationError[] => {
  const errors: ImportValidationError[] = [];
  const { data, rowIndex } = property;

  // Required field validations
  if (!data.property_address || data.property_address.trim() === '') {
    errors.push({
      row: rowIndex,
      field: 'Property Address',
      value: data.property_address || '',
      error: 'Property address is required'
    });
  }

  if (!data.purchase_price || data.purchase_price <= 0) {
    errors.push({
      row: rowIndex,
      field: 'Purchase Price',
      value: data.purchase_price?.toString() || '',
      error: 'Purchase price must be greater than 0'
    });
  }

  if (!data.down_payment || data.down_payment < 0) {
    errors.push({
      row: rowIndex,
      field: 'Down Payment',
      value: data.down_payment?.toString() || '',
      error: 'Down payment must be 0 or greater'
    });
  }

  if (!data.loan_amount || data.loan_amount < 0) {
    errors.push({
      row: rowIndex,
      field: 'Loan Amount',
      value: data.loan_amount?.toString() || '',
      error: 'Loan amount must be 0 or greater'
    });
  }

  if (!data.interest_rate || data.interest_rate < 0 || data.interest_rate > 50) {
    errors.push({
      row: rowIndex,
      field: 'Interest Rate (%)',
      value: data.interest_rate?.toString() || '',
      error: 'Interest rate must be between 0 and 50%'
    });
  }

  if (!data.loan_term || data.loan_term <= 0 || data.loan_term > 50) {
    errors.push({
      row: rowIndex,
      field: 'Loan Term (Years)',
      value: data.loan_term?.toString() || '',
      error: 'Loan term must be between 1 and 50 years'
    });
  }

  if (data.annual_taxes < 0) {
    errors.push({
      row: rowIndex,
      field: 'Annual Taxes',
      value: data.annual_taxes?.toString() || '',
      error: 'Annual taxes cannot be negative'
    });
  }

  if (data.annual_insurance < 0) {
    errors.push({
      row: rowIndex,
      field: 'Annual Insurance',
      value: data.annual_insurance?.toString() || '',
      error: 'Annual insurance cannot be negative'
    });
  }

  // Logical validations
  if (data.purchase_price && data.down_payment && data.down_payment > data.purchase_price) {
    errors.push({
      row: rowIndex,
      field: 'Down Payment',
      value: data.down_payment.toString(),
      error: 'Down payment cannot exceed purchase price'
    });
  }

  if (data.purchase_price && data.loan_amount && data.down_payment) {
    const expectedLoanAmount = data.purchase_price - data.down_payment;
    const tolerance = 1000; // Allow $1000 tolerance for rounding
    
    if (Math.abs(data.loan_amount - expectedLoanAmount) > tolerance) {
      errors.push({
        row: rowIndex,
        field: 'Loan Amount',
        value: data.loan_amount.toString(),
        error: `Loan amount should be approximately ${expectedLoanAmount.toLocaleString()} (Purchase Price - Down Payment)`
      });
    }
  }

  return errors;
};

export const findDuplicateProperties = (
  newProperties: ParsedProperty[],
  existingProperties: PropertyResponse[]
): ParsedProperty[] => {
  const duplicates: ParsedProperty[] = [];

  for (const newProperty of newProperties) {
    const isDuplicate = existingProperties.some(existing =>
      existing.property_address.toLowerCase().trim() === 
      newProperty.data.property_address.toLowerCase().trim() &&
      existing.purchase_price === newProperty.data.purchase_price
    );

    if (isDuplicate) {
      duplicates.push(newProperty);
    }
  }

  return duplicates;
};

export const processCSVImport = (
  csvContent: string,
  existingProperties: PropertyResponse[]
): ImportResult => {
  try {
    // Parse CSV content
    const rows = parseCSVContent(csvContent);
    
    if (rows.length < 2) {
      throw new Error('CSV file must contain at least a header row and one data row');
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);

    // Validate headers
    const headerValidation = validateCSVHeaders(headers);
    if (!headerValidation.valid) {
      throw new Error(`Invalid CSV format: ${headerValidation.errors.join(', ')}`);
    }

    // Parse and validate each row
    const parsedProperties: ParsedProperty[] = [];
    const allErrors: ImportValidationError[] = [];

    dataRows.forEach((row, index) => {
      // Skip empty rows
      if (row.every(cell => !cell || cell.trim() === '')) {
        return;
      }

      const parsedProperty = parsePropertyFromRow(row, headers, index + 2); // +2 for header row and 0-based index
      const validationErrors = validateProperty(parsedProperty);

      if (validationErrors.length === 0) {
        parsedProperties.push(parsedProperty);
      } else {
        allErrors.push(...validationErrors);
      }
    });

    // Find duplicates
    const duplicates = findDuplicateProperties(parsedProperties, existingProperties);
    const validProperties = parsedProperties.filter(
      prop => !duplicates.some(dup => dup.rowIndex === prop.rowIndex)
    );

    return {
      valid: validProperties,
      invalid: allErrors,
      duplicates,
      summary: {
        totalRows: dataRows.filter(row => !row.every(cell => !cell || cell.trim() === '')).length,
        validRows: validProperties.length,
        invalidRows: allErrors.length > 0 ? Math.max(1, new Set(allErrors.map(e => e.row)).size) : 0,
        duplicateRows: duplicates.length
      }
    };
  } catch (error) {
    throw new Error(`Failed to process CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper to create sample CSV content for download
export const generateSampleCSV = (): string => {
  const sampleData = [
    EXPECTED_HEADERS.join(','),
    [
      '"123 Main St"',
      'Denver',
      'CO',
      '80202',
      'Buy & Hold',
      'Single Family',
      '350000',
      '70000',
      '280000',
      '6.5',
      '30',
      '4200',
      '1200',
      '0',
      '2800',
      '5', // Vacancy Rate
      '3', // Maintenance Rate
      '8', // Management Rate
      '', // After Repair Value
      '', // Holding Period
      '', // Selling Costs
      '8000', // Renovation - Roof
      '5000', // Renovation - Floors
      '2000', // Renovation - Painting
      '0', // Renovation - General
      '15000', // Total Renovation Costs
      '', // Monthly Cash Flow (calculated)
      '', // Annual Cash Flow (calculated)
      '', // Cash-on-Cash Return (calculated)
      '', // Cap Rate (calculated)
      '' // Date Added (auto-generated)
    ].join(','),
    [
      '"456 Oak Ave"',
      'Boulder',
      'CO',
      '80301',
      'Fix & Flip',
      'Condo',
      '280000',
      '56000',
      '224000',
      '7.0',
      '30',
      '3600',
      '800',
      '150',
      '2200',
      '0', // Vacancy Rate (not applicable for flip)
      '0', // Maintenance Rate (not applicable for flip)
      '0', // Management Rate (not applicable for flip)
      '320000', // After Repair Value
      '6', // Holding Period (months)
      '15000', // Selling Costs
      '10000', // Renovation - Roof
      '8000', // Renovation - Floors
      '3000', // Renovation - Painting
      '4000', // Renovation - General
      '25000', // Total Renovation Costs
      '', // Monthly Cash Flow (calculated)
      '', // Annual Cash Flow (calculated)
      '', // Cash-on-Cash Return (calculated)
      '', // Cap Rate (calculated)
      '' // Date Added (auto-generated)
    ].join(',')
  ];

  return sampleData.join('\n');
};